import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { AlertTriangle, Pencil, RefreshCw, RotateCcw, Search, Star } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Skeleton } from '@/components/insights/Skeleton';
import { FxTable, fxService } from '@/services/fx.service';
import { timeAgo } from '@/pages/industry-configs/industry-meta';

// Currencies clients actually bill in — shown first.
const PINNED = ['AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'PKR', 'EGP', 'EUR', 'GBP', 'INR'];

/**
 * Daily rates (1 USD = x) used only to SHOW totals in a company's currency —
 * Ads Overview, A/B Compare, the documents cash-flow strip. Nothing sent to
 * an ad platform is ever converted. An override wins over the automatic rate.
 */
export default function ExchangeRates() {
  const [table, setTable] = useState<FxTable | null>(null);
  const [q, setQ] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fxService.table().then(setTable).catch(() => { /* interceptor toasts */ }); }, []);

  const rows = useMemo(() => {
    if (!table) return [];
    const all = Object.keys({ ...table.auto, ...table.overrides }).filter((c) => c !== table.base).sort();
    const term = q.trim().toUpperCase();
    const list = term ? all.filter((c) => c.includes(term)) : all;
    return [...PINNED.filter((c) => list.includes(c)), ...list.filter((c) => !PINNED.includes(c))];
  }, [table, q]);

  const refresh = async () => {
    setRefreshing(true);
    try { setTable(await fxService.refresh()); toast.success('Rates refreshed'); }
    catch { /* interceptor toasts */ } finally { setRefreshing(false); }
  };

  const startEdit = (cur: string) => {
    setEditing(cur);
    setDraft(String(table?.overrides[cur]?.rate ?? table?.auto[cur] ?? ''));
    setNote(table?.overrides[cur]?.note ?? '');
  };

  const saveOverride = async (cur: string) => {
    const rate = Number(draft);
    if (!(rate > 0)) { toast.error('Enter a positive rate'); return; }
    setSaving(true);
    try {
      setTable(await fxService.setOverride(cur, rate, note.trim() || undefined));
      setEditing(null);
      toast.success(`${cur} now uses your rate`);
    } catch { /* interceptor toasts */ } finally { setSaving(false); }
  };

  const removeOverride = async (cur: string) => {
    try { setTable(await fxService.removeOverride(cur)); toast.success(`${cur} back on the automatic rate`); }
    catch { /* interceptor toasts */ }
  };

  const overrides = table ? Object.keys(table.overrides).length : 0;

  return (
    <>
      <Topbar
        title="Exchange rates"
        breadcrumb="Platform / Exchange rates"
        description="Used to show totals in each company's currency. Ad platforms are always billed in their own currency."
        right={<Button variant="secondary" iconLeft={<RefreshCw size={15} className={clsx(refreshing && 'animate-spin')} />} loading={refreshing} onClick={refresh}>Refresh now</Button>}
      />
      <div className="mx-auto w-full max-w-5xl space-y-5 px-4 py-6 sm:px-7">
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Last update" value={table?.fetchedAt ? timeAgo(table.fetchedAt) : '—'} detail={table?.source ? `From ${table.source}, daily at 03:15 UTC` : 'Updates daily'} />
          <Stat label="Currencies" value={table ? String(Object.keys(table.rates).length - 1) : '—'} detail="Rate per 1 USD" />
          <Stat label="Manual overrides" value={table ? String(overrides) : '—'} detail="Win over the automatic rate" />
        </div>

        {table?.lastError && (
          <div className="flex items-start gap-2 rounded-xl border border-warning/40 bg-warning-soft px-4 py-3 text-xs text-text-secondary">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
            <span>The last automatic refresh failed ({table.lastError}). The previous rates stay in use.</span>
          </div>
        )}

        <div className="rounded-2xl border border-border bg-surface shadow-soft">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
            <div className="w-full max-w-xs">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a currency, e.g. PKR" iconLeft={<Search size={14} />} />
            </div>
            <span className="text-xs text-text-muted">1 USD = … · common client currencies first</span>
          </div>
          {!table ? (
            <div className="space-y-2 p-5">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}</div>
          ) : (
            <div className="divide-y divide-border">
              {rows.map((cur) => {
                const o = table.overrides[cur];
                const auto = table.auto[cur];
                const isEditing = editing === cur;
                return (
                  <div key={cur} className="flex flex-wrap items-center gap-3 px-5 py-2.5">
                    <div className="flex w-24 items-center gap-1.5">
                      {PINNED.includes(cur) && <Star size={11} className="fill-primary text-primary" />}
                      <span className="font-mono text-sm font-semibold text-text-primary">{cur}</span>
                    </div>
                    {isEditing ? (
                      <div className="flex flex-1 flex-wrap items-center gap-2">
                        <div className="w-36"><Input type="number" step="any" min={0} value={draft} onChange={(e) => setDraft(e.target.value)} aria-label={`${cur} per USD`} autoFocus /></div>
                        <div className="min-w-[180px] flex-1"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (optional), e.g. bank rate" maxLength={200} /></div>
                        <Button size="sm" loading={saving} onClick={() => saveOverride(cur)}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <>
                        <div className="min-w-0 flex-1">
                          <span className="text-sm tabular-nums text-text-primary">{fmt(o?.rate ?? auto)}</span>
                          {o && (
                            <span className="ml-2 rounded bg-primary-soft px-1.5 py-0.5 text-[10px] font-semibold text-primary" title={o.note ?? undefined}>
                              Manual · auto {fmt(auto)}
                            </span>
                          )}
                          {o?.note && <div className="truncate text-2xs text-text-muted">{o.note}</div>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" iconLeft={<Pencil size={13} />} onClick={() => startEdit(cur)}>{o ? 'Edit' : 'Override'}</Button>
                          {o && <Button size="sm" variant="ghost" iconLeft={<RotateCcw size={13} />} onClick={() => removeOverride(cur)}>Use automatic</Button>}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
              {rows.length === 0 && <div className="px-5 py-10 text-center text-sm text-text-muted">No currency matches “{q}”.</div>}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const fmt = (n?: number) => (n === undefined ? '—' : n >= 100 ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : n.toLocaleString(undefined, { maximumFractionDigits: 4 }));

function Stat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-4 shadow-soft">
      <div className="text-xs text-text-muted">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-text-primary">{value}</div>
      <div className="mt-0.5 text-2xs text-text-muted">{detail}</div>
    </div>
  );
}
